import {
  Storefront, Wallet, Wrench, Users, UsersThree, ChatCircleDots, CreditCard, Receipt,
  Hourglass, ArrowsClockwise, CurrencyInr, CalendarCheck, ChartBar, Rocket,
} from '@phosphor-icons/react';

export const QUICK_START_STEPS = [
  {
    id: 'company', icon: Storefront, color: '#2563EB', to: '/settings',
    title: 'Set up your company', where: 'Management → Settings',
    points: [
      'Open Settings and fill Company Profile — name, address, phone, email, GSTIN and upload your logo.',
      'In Invoice Numbering, set your own prefix (e.g. CSC/26-27/) and starting number.',
      'Add your Branches if you run more than one centre.',
      'Click Save at the bottom of the page.',
    ],
    tip: 'Your logo and address appear on every printed invoice and receipt.',
  },
  {
    id: 'accounts', icon: Wallet, color: '#10B981', to: '/reports',
    title: 'Add your money accounts', where: 'Analytics → Reports → Add Account',
    points: [
      'Open Reports and click "Add Account".',
      'Create one account for each place money is kept — Cash, Bank, UPI / Wallet, Credit Card.',
      'Enter the current balance as the opening balance.',
      'Use the eye / pencil icons on an account card to view its ledger or edit it.',
    ],
    tip: 'Every receipt, expense and transfer must pick one of these accounts, so add them first.',
  },
  {
    id: 'services', icon: Wrench, color: '#7C3AED', to: '/services',
    title: 'Add Service Groups & Services', where: 'Masters → Service Groups / Services',
    points: [
      'In Service Groups, create groups like "PAN", "Aadhaar", "Certificates", "Bill Payments".',
      'Open Services → "Add Service". Enter Code, Name, Service Charge and Govt Fee, and pick a Group.',
      'Tick "Requires Follow-up / Processing" for services that need tracking (e.g. applications).',
      'Tick "Recurring" for subscription-type services and choose the renewal interval.',
      'Add variants / quantity-based pricing if one service has different rates.',
    ],
    tip: 'Services you add here appear in the invoice service list for quick billing.',
  },
  {
    id: 'customers', icon: Users, color: '#DB2777', to: '/customers',
    title: 'Add Customer Groups & Customers', where: 'Masters → Customer Groups / Customer',
    points: [
      'Create Customer Groups (e.g. "Walk-in", "Regular", "Business") to organise customers.',
      'Open Customer → "Add Customer". Enter name, mobile number and other details.',
      'Search any customer by name or mobile. Open the ledger to see all bills and payments.',
      'Customers can also be added directly while creating an invoice.',
    ],
    tip: 'Always save the correct mobile number — it is used for WhatsApp messages.',
  },
  {
    id: 'staff', icon: UsersThree, color: '#E11D48', to: '/staff',
    title: 'Add Staff, Shifts & Permissions', where: 'Staff Management',
    points: [
      'Staff List → add each staff member with their login email and password.',
      'Shift Settings → define working hours / shifts.',
      'Permissions → choose which pages each staff can open (e.g. hide daily totals or reports).',
      'Settings → Staff Policies to set rules that apply to all staff.',
    ],
    tip: 'Staff log in from the same login page using the email and password you set.',
  },
  {
    id: 'whatsapp', icon: ChatCircleDots, color: '#25D366', to: '/settings',
    title: 'WhatsApp messages & templates', where: 'Management → Settings',
    points: [
      'WhatsApp Messaging — edit the invoice message sent to customers.',
      'Status Intimations — edit messages for Submitted, Approved, Under Process and Docs. Pending.',
      'Use the placeholders shown (customer name, invoice number, amount, due) — they fill automatically.',
    ],
    tip: 'Messages open in WhatsApp on your phone / computer — just press Send.',
  },
  {
    id: 'payments', icon: CreditCard, color: '#4F46E5', to: '/settings',
    title: 'Online payments (optional)', where: 'Settings → Online Payments',
    points: [
      'Connect your own Razorpay account by entering your Key ID and Key Secret, then Verify.',
      'After connecting, a "Pay Online" link is available on invoices with due amount.',
      'Money paid by customers goes directly to your own Razorpay / bank account.',
    ],
    tip: 'Skip this step if you collect only cash / UPI at the counter.',
  },
  {
    id: 'invoice', icon: Receipt, color: '#4F46E5', to: '/invoices',
    title: 'Create Invoice / Token', where: 'Operations → New Invoice/Token',
    points: [
      'Click "New Invoice", select or add the customer.',
      'Add services, quantity and price. The invoice number comes automatically.',
      'Enter the amount paid and payment mode / account. Any balance is saved as due.',
      'Save, then Print or send the invoice on WhatsApp.',
      'Search invoices by number, name or mobile; edit or view any invoice from the list.',
    ],
    tip: 'Shortcut: Ctrl + Shift + N opens New Invoice from anywhere.',
  },
  {
    id: 'followup', icon: Hourglass, color: '#D97706', to: '/processing',
    title: 'My Work & Follow-Up', where: 'Operations → My Work / Follow-Up',
    points: [
      'Services marked "Requires Follow-up" appear in Follow-Up after billing.',
      'Search a customer by name or mobile and update the work status from the dropdown.',
      'Click the WhatsApp icon next to the status to send the current status to the customer.',
      'My Work shows the jobs assigned to the logged-in staff.',
    ],
    tip: 'Keep statuses updated so customers always know their application progress.',
  },
  {
    id: 'recurring', icon: ArrowsClockwise, color: '#0284C7', to: '/recurring',
    title: 'Recurring services & To-Do Notes', where: 'Operations → Recurring / To-Do Notes',
    points: [
      'Recurring lists customers whose subscription services are due for renewal.',
      'Contact them and create a fresh invoice when they renew.',
      'To-Do Notes — save reminders and tasks for yourself or your team.',
    ],
    tip: 'Check Recurring every morning so no renewal is missed.',
  },
  {
    id: 'finance', icon: CurrencyInr, color: '#10B981', to: '/receipt',
    title: 'Receipts, Refunds, Income, Payment & Transfer', where: 'Finance',
    points: [
      'Receipt — collect pending dues from customers against their invoices.',
      'Refund — return money or advance to a customer.',
      'Income — record other income (commission, incentives).',
      'Payment — pay expenses (rent, electricity, salary…), creditors (loan / card) or capital drawings from Cash / Bank.',
      'Transfer — move money between your accounts (e.g. Cash → Bank deposit).',
    ],
    tip: 'Record every money movement here so your account balances always match.',
  },
  {
    id: 'hr', icon: CalendarCheck, color: '#15803D', to: '/staff/attendance',
    title: 'Attendance, Leave, Salary & Performance', where: 'Staff Management',
    points: [
      'Attendance — mark daily attendance of staff.',
      'Leave — approve or reject leave requests.',
      'Work Log — see the work done by each staff member.',
      'Salary & Incentives — calculate and record monthly salary and incentives.',
      'Performance & Staff Reports — compare staff work and collections.',
    ],
    tip: 'Accurate attendance makes salary calculation automatic and correct.',
  },
  {
    id: 'reports', icon: ChartBar, color: '#0D9488', to: '/reports',
    title: 'Dashboard & Reports', where: 'Dashboard / Analytics',
    points: [
      'Dashboard — today\u2019s sales, collections, dues and pending work at a glance.',
      'Reports — filter transactions by date and account; see opening and closing balance.',
      'Daily Transaction Report — full day summary to close the counter every evening.',
    ],
    tip: 'Match the Daily Transaction Report with cash in hand before closing the day.',
  },
  {
    id: 'billing', icon: Rocket, color: '#F97316', to: '/billing',
    title: 'Plan, Billing & shortcuts', where: 'Management → Plan & Billing',
    points: [
      'Plan & Billing — see your trial / plan expiry and renew online in one click.',
      'You get an email reminder 7 days and 1 day before your plan expires.',
      'Press "?" anywhere in the app to see all keyboard shortcuts.',
    ],
    tip: 'You can reopen this guide anytime from the "Quick Start Guide" button at the top.',
  },
];
